import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  Palette,
  Globe,
  Code2,
  Tag,
  HeartHandshake,
  SlidersHorizontal,
  CheckCircle2,
} from 'lucide-react'
import {
  Container,
  PageHero,
  SectionHeading,
  FeatureCard,
  CtaBand,
  fadeUp,
} from '@/components/umbrella/ui'
import DeploymentExamples from '@/components/umbrella/DeploymentExamples'

export const Route = createFileRoute('/how-it-works')({
  component: HowItWorksPage,
})

const STEPS = [
  {
    title: 'Tell us who you serve',
    body: 'One conversation about your audience and where you want the program to live.',
  },
  {
    title: 'We build it in your brand',
    body: 'Themed to you, loaded with the scams that matter to your audience, and routed to your own hotlines.',
  },
  {
    title: 'You go live',
    body: 'On your own web address, or dropped straight into the site or app you already have.',
  },
  {
    title: 'We keep it sharp',
    body: 'Scams keep changing, so we keep the content current and send you privacy-first usage insights.',
  },
]

/** The short list of things we actually need from a partner. */
const INPUTS = [
  'Your logo and colors',
  'Your reporting and help-line numbers',
  'The scams that matter most to your people',
  'Where you want it to live',
]

const DEPLOYMENT = [
  {
    Icon: Palette,
    title: 'Your branding',
    body: 'Your logo, colors, name, and tone throughout. To your audience it looks and feels like a tool you built yourself.',
  },
  {
    Icon: Globe,
    title: 'Your web address',
    body: 'Host it on your own domain, like safety.yourbank.org or yoursite.org/scam-center, so people never leave your brand.',
  },
  {
    Icon: Code2,
    title: 'Embedded in your site or app',
    body: 'Drop ScamDefense into your existing website, online banking, or mobile app so the education lives where your audience already is.',
  },
  {
    Icon: Tag,
    title: 'Fully white-label',
    body: 'Run it with no ScamDefense branding at all, delivered entirely under your name as if it were your own product.',
  },
  {
    Icon: HeartHandshake,
    title: 'Co-branded',
    body: 'Or pair your identity with ScamDefense to signal a credible, trusted partnership behind the education.',
  },
  {
    Icon: SlidersHorizontal,
    title: 'Tailored content & routing',
    body: 'Scam examples, reporting hotlines, and language chosen for your audience, not a generic template.',
  },
]

function HowItWorksPage() {
  return (
    <div className="bg-white">
      <PageHero
        label="How it works"
        title="We set it up."
        highlight="You launch it."
        blurb="A ScamDefense program is built for you, not by you. Tell us who you serve and we handle the rest, themed in your brand, routed to your hotlines, and live in a matter of weeks."
        primary={{ label: 'Talk to our team', href: '#contact' }}
      />

      <Container className="py-20">
        <SectionHeading
          label="The process"
          title="From first conversation to live program"
          blurb="One call to tell us who you serve. We handle the build, the updates, and the threat research from there."
        />
        <div className="mt-14 grid grid-cols-1 gap-y-12 lg:grid-cols-4 lg:gap-x-10 lg:gap-y-0 lg:divide-x lg:divide-slate-200">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="lg:ps-10 lg:first:ps-0"
            >
              <span className="text-3xl font-semibold text-blue-600/25 tabular-nums">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="mt-3 text-xl font-semibold tracking-tight text-slate-900">
                {s.title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-slate-600">{s.body}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          {...fadeUp}
          className="mt-12 rounded-3xl border border-slate-200/80 bg-slate-50 p-8 sm:p-10"
        >
          <div className="grid gap-8 lg:grid-cols-[1fr_1.3fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold text-blue-600">Your side of it</p>
              <h3 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">
                Four small things, then we take it from there
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                You won’t need to write content, hire fraud expertise, or
                staff an engineering project.
              </p>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {INPUTS.map(item => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 rounded-xl border border-slate-200/80 bg-white px-4 py-3 text-[15px] font-medium text-slate-700"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </Container>

      <section className="bg-slate-50 py-20">
        <Container>
          <SectionHeading
            label="Deployment options"
            title="It carries your name, not ours"
            blurb="ScamDefense is not a fixed product you point people to. We shape how it looks, where it lives, and what it says, so it fits the brand and channels your audience already trusts. Combine any of these."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {DEPLOYMENT.map(d => (
              <FeatureCard key={d.title} {...d} />
            ))}
          </div>

          <p className="mt-14 text-center text-sm font-semibold text-slate-500">
            What that looks like in practice
          </p>
          <DeploymentExamples />
        </Container>
      </section>

      <CtaBand />
    </div>
  )
}
